import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';

export type ErrorBoundaryProps = {
  /**
   * The failing area, as the subject of a sentence -- it is rendered as
   * "{region} could not be displayed" and prefixes the console log, so it is
   * both what the user reads and what identifies the failure in a feedback
   * report.
   */
  region: string;
  /**
   * The identity of what the subtree is rendering (a route path, an incident
   * id). Changing it clears a caught error, so moving to another incident
   * leaves the region working instead of stranded on the fallback of the
   * record that broke it. A change while the subtree is healthy does nothing.
   * Omit for regions with no such identity.
   */
  resetKey?: string | number;
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  error: Error | null;
  /**
   * Bumped by Retry and used as the subtree's key, so a recovery is a fresh
   * mount rather than a re-render of whatever state threw.
   */
  generation: number;
};

/**
 * Contains a render-time throw to one region of the page.
 *
 * This is a containment mechanism, not a fallback: the failure is always
 * reported to the console and always shown to the user as an error, because
 * this app is used to run active disaster response and a region that quietly
 * renders as empty would be read as "no threats", "no outages", "no exposed
 * structures".
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, generation: 0 };

  static getDerivedStateFromError(error: unknown): Pick<ErrorBoundaryState, 'error'> {
    // A throw carries any value, not necessarily an Error; normalising here
    // keeps the fallback's message and the state type honest.
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // There is no error-reporting service in this app. console.error is the
    // reporting path: feedback/consoleLogBuffer wraps console.error and
    // window's error events into the session log that a user's feedback
    // submission carries, and in production React does not re-raise an error a
    // boundary caught -- so without this line the failure would reach neither
    // the console nor a report.
    console.error(
      `Error boundary caught a render failure in ${this.props.region}:`,
      error,
      info.componentStack
    );
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.error != null && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private handleRetry = (): void => {
    this.setState(state => ({ error: null, generation: state.generation + 1 }));
  };

  render(): React.ReactNode {
    const { error } = this.state;

    if (error != null) {
      const detail = error.message.trim();

      return (
        <Box
          sx={{
            p: 2,
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
        >
          <Alert
            severity="error"
            variant="filled"
            elevation={6}
            sx={{ width: '100%' }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<ReplayIcon />}
                onClick={this.handleRetry}
              >
                Retry
              </Button>
            }
          >
            <AlertTitle sx={{ mb: 0.5 }}>{this.props.region} could not be displayed</AlertTitle>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {detail.length > 0 ? detail : error.name}
            </Typography>
          </Alert>
        </Box>
      );
    }

    return <React.Fragment key={this.state.generation}>{this.props.children}</React.Fragment>;
  }
}
